import { Box, Typography } from "@mui/material";
import { shape } from "@app/design-tokens";
import type { ReactNode } from "react";

import type { SvgIconComponent } from "@mui/icons-material";

/**
 * 空狀態。
 *
 * 三種情境共用：查無資料、還沒有資料、以及「這一頁還沒實作」。
 * 後者刻意也走同一個元件而不是隨手寫一段文字 —— 未實作的頁面看起來要
 * 像產品的一部分，而不是像壞掉了。
 */
interface EmptyStateProps {
  icon: SvgIconComponent;
  title: string;
  description?: ReactNode;
  /** 按鈕或連結 */
  action?: ReactNode;
}

export function EmptyState({ icon: Icon, title, description, action }: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1.5,
        px: 3,
        py: 8,
        border: 1,
        borderStyle: "dashed",
        borderColor: "divider",
        borderRadius: `${shape.large}px`,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 52,
          height: 52,
          borderRadius: `${shape.medium}px`,
          bgcolor: "action.hover",
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon />
      </Box>

      <Typography variant="h6" component="p">
        {title}
      </Typography>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
          {description}
        </Typography>
      )}

      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
}
